import { useMemo } from "react";

import { DownloadIcon } from "@/assets/icons";
import { Button } from "@/atoms";
import { strings } from "@/constants";
import { ButtonVariantEnum } from "@/enums";
import type { BenchmarkResultPayloadType } from "@/types";
import { renderBenchmarkXlsxHtmlHandler } from "@/utils";

interface BenchmarkResultPreviewPropsInterface {
    industry?: string,
    jobTitle?: string,
    onDownload: () => void,
    onReset: () => void,
    result: BenchmarkResultPayloadType,
    seniority?: string,
}

const eyebrowClass = `
    inline-flex
    items-center
    gap-2
    text-[11px]
    font-semibold
    tracking-[0.28em]
    text-primary/70
    uppercase
`;

const metaCardClass = `
    flex
    flex-col
    gap-0.5
    rounded-xl
    border
    border-primary/15
    bg-background/70
    px-3
    py-2
    transition-colors
    duration-200
    hover:border-primary/40
    hover:bg-background
`;

const metaLabelClass = `
    text-[10px]
    font-semibold
    tracking-[0.22em]
    text-primary/60
    uppercase
`;

const metaValueClass = `
    truncate
    text-[13px]
    font-semibold
    text-text-primary
`;

const tableShellClass = `
    benchmark-xlsx-preview
    [&_table]:w-full
    [&_table]:border-collapse
    [&_table]:font-mono
    [&_table]:text-[13px]
    [&_td]:border
    [&_td]:border-primary/10
    [&_td]:px-2.5
    [&_td]:py-1.5
    [&_td]:align-top
    [&_td]:text-text-primary
    [&_td:empty]:bg-background/30
    [&_tr:nth-child(2n)_td]:bg-background/40
    [&_tr:hover_td]:bg-primary/5
`;

export const BenchmarkResultPreview = ({
    industry,
    jobTitle,
    onDownload,
    onReset,
    result,
    seniority,
}: BenchmarkResultPreviewPropsInterface) => {
    const previewHtml = useMemo(
        () => renderBenchmarkXlsxHtmlHandler({
            industry,
            jobTitle,
            result,
            seniority,
        }),
        [
            industry,
            jobTitle,
            result,
            seniority,
        ],
    );

    const displayJobTitle = jobTitle?.trim() || result.jobTitle?.trim() || "";

    const displayIndustry = industry?.trim() || result.industry?.trim() || "";

    const displaySeniority = seniority?.trim() || result.seniority?.trim() || "";

    const branchCount = result.selectedBranches?.length ?? 0;

    const environmentCount = result.environmentStatus?.length ?? 0;

    return (
        <div
            className="
                form-rise
                mx-auto
                flex
                h-full
                min-h-0
                w-full
                max-w-6xl
                flex-col
                text-left
            "
        >
            <div
                className="
                    relative
                    flex
                    h-full
                    min-h-0
                    flex-col
                    overflow-hidden
                    rounded-3xl
                    border
                    border-primary/10
                    bg-surface
                    shadow-[0_30px_70px_-45px_rgba(0,37,186,0.45)]
                "
            >
                <div
                    aria-hidden
                    className="
                        absolute
                        inset-x-0
                        top-0
                        h-px
                        bg-gradient-to-r
                        from-transparent
                        via-primary/40
                        to-transparent
                    "
                />
                <header
                    className="
                        relative
                        shrink-0
                        border-b
                        border-primary/10
                        px-5
                        pt-4
                        pb-4
                        sm:px-8
                        sm:pt-5
                        sm:pb-5
                    "
                >
                    <div
                        className="
                            flex
                            flex-wrap
                            items-start
                            justify-between
                            gap-4
                        "
                    >
                        <div className="min-w-0 flex-1">
                            <div className={eyebrowClass}>
                                <span
                                    aria-hidden
                                    className="
                                        inline-block
                                        size-1.5
                                        rounded-full
                                        bg-primary
                                    "
                                />
                                <span>{strings.result.previewTitle}</span>
                            </div>
                            {displayJobTitle && (
                                <h2
                                    className="
                                        mt-1.5
                                        truncate
                                        text-xl
                                        font-bold
                                        leading-tight
                                        tracking-tight
                                        text-text-primary
                                        sm:text-2xl
                                    "
                                >
                                    {displayJobTitle}
                                </h2>
                            )}
                        </div>
                        <div
                            className="
                                hidden
                                shrink-0
                                items-center
                                gap-2
                                rounded-full
                                border
                                border-primary/15
                                bg-background/60
                                px-3.5
                                py-1.5
                                font-mono
                                text-[11px]
                                tracking-[0.16em]
                                text-primary/80
                                uppercase
                                sm:inline-flex
                            "
                        >
                            <span className="size-1.5 rounded-full bg-primary" />
                            <span className="tabular-nums">{`${branchCount} · ${environmentCount}`}</span>
                            <span className="text-primary/40">{"branches · env"}</span>
                        </div>
                    </div>
                    <dl
                        className="
                            mt-3
                            grid
                            grid-cols-1
                            gap-2
                            sm:grid-cols-3
                            sm:mt-4
                        "
                    >
                        <div className={metaCardClass}>
                            <dt className={metaLabelClass}>{strings.benchmark.jobTitleLabel}</dt>
                            <dd className={metaValueClass}>{displayJobTitle || "—"}</dd>
                        </div>
                        <div className={metaCardClass}>
                            <dt className={metaLabelClass}>{strings.benchmark.industryLabel}</dt>
                            <dd className={metaValueClass}>{displayIndustry || "—"}</dd>
                        </div>
                        <div className={metaCardClass}>
                            <dt className={metaLabelClass}>{strings.benchmark.seniorityLabel}</dt>
                            <dd
                                className={`
                                    ${metaValueClass}
                                    uppercase
                                    tracking-wide
                                `}
                            >
                                {displaySeniority || "—"}
                            </dd>
                        </div>
                    </dl>
                </header>
                <div
                    className="
                        relative
                        min-h-0
                        flex-1
                        overflow-auto
                        bg-white
                        px-3
                        py-3
                        sm:px-5
                        sm:py-4
                    "
                >
                    <div
                        className={tableShellClass}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                </div>
                <div
                    className="
                        z-10
                        flex
                        shrink-0
                        flex-col
                        items-stretch
                        gap-2
                        border-t
                        border-primary/10
                        bg-surface
                        px-5
                        py-3
                        sm:flex-row
                        sm:items-center
                        sm:justify-end
                        sm:gap-3
                        sm:px-8
                        sm:py-3.5
                    "
                >
                    <Button onClick={onReset}>
                        {strings.result.buttonReset}
                    </Button>
                    <Button
                        className="
                            inline-flex
                            items-center
                            justify-center
                            gap-2
                            tracking-[0.12em]
                            uppercase
                        "
                        variant={ButtonVariantEnum.PRIMARY}
                        onClick={onDownload}
                    >
                        <DownloadIcon className="size-4" />
                        {strings.result.xlsxButton}
                    </Button>
                </div>
            </div>
        </div>
    );
};
